/**
 * Powered by Dink card (DNKADM-0500 / DNKAPI-0717) — the "Show 'Powered by Dink' on your
 * booking site" switch on Tenant settings. Default on. One switch that saves ON ITS OWN when
 * flipped, like the Coach hours card: `PUT /v2/admin/tenant-settings` carrying only
 * `poweredByLinkEnabled`. Not optimistic — the switch shows the PUT response value.
 * OWNER/ADMIN only (the route is `RequireAdmin`-gated; the server refuses others).
 */
import { useState } from 'react';
import { Box, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';
import { Icon } from '@/theme';
import type { ShowToast } from '@/components/toastContext';
import { putTenantSettings } from '@/api/tenantSettings';
import { classifyFormError } from '@/api/screenError';

interface PoweredByCardProps {
  /** The loaded `poweredByLinkEnabled` value. */
  initialEnabled: boolean;
  /** Raise a non-blocking toast. */
  showToast: ShowToast;
}

export const POWERED_BY_SWITCH_LABEL = "Show 'Powered by Dink' on your booking site";

export function PoweredByCard({ initialEnabled, showToast }: PoweredByCardProps) {
  // The SAVED value; only a PUT response moves it.
  const [enabled, setEnabled] = useState(initialEnabled);
  const [saving, setSaving] = useState(false);

  async function handleChange(next: boolean) {
    setSaving(true);
    try {
      const saved = await putTenantSettings({ poweredByLinkEnabled: next });
      setEnabled(saved.poweredByLinkEnabled);
      showToast(
        saved.poweredByLinkEnabled
          ? "'Powered by Dink' link turned on"
          : "'Powered by Dink' link turned off",
        'success',
      );
    } catch (err) {
      const view = classifyFormError(err, 'That value isn’t valid.', {
        toastFallback: 'The settings couldn’t be saved. Please try again.',
        conflictInline: false,
      });
      showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1.5,
            p: '16px 20px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'background.default',
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            <Icon name="link" sx={{ fontSize: 20 }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Booking site link</Typography>
        </Stack>
        <Box sx={{ p: '18px 20px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(event) => void handleChange(event.target.checked)}
                disabled={saving}
              />
            }
            label={POWERED_BY_SWITCH_LABEL}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            A small link at the bottom of your member booking site.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
